import { Link } from 'react-router-dom'

import { fetchHealth, fetchMeta } from '@/api'
import { Chip, PageHead, PageShell, Section } from '@/components/common'
import { useAsync } from '@/hooks'
import { useI18n } from '@/i18n'
import { FIP_WIZARD_URL } from '@/links'

const endpoints = [
    '/api/meta',
    '/api/principles',
    '/api/resource-types',
    '/api/organizations',
    '/api/resources',
    '/api/fips',
    '/api/stats',
    '/api/matrix',
    '/api/search?q=doi',
]

export function About() {
    const { t, localized, formatDate } = useI18n()
    const meta = useAsync(fetchMeta, [])
    const health = useAsync(fetchHealth, [])

    return (
        <PageShell>
            <PageHead title={t('about.title')} />

            <Section title={t('about.whatTitle')}>
                <div className="card">
                    <p>{t('about.what')}</p>
                    <Link to="/glossary">{t('glossary.title')} →</Link>
                </div>
            </Section>

            <Section title={t('about.authoringTitle')}>
                <div className="card">
                    <p>{t('about.authoring')}</p>
                    <a
                        className="button"
                        href={FIP_WIZARD_URL}
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        {t('about.authoringCta')} ↗
                    </a>
                </div>
            </Section>

            <Section title={t('about.dataTitle')}>
                <div className="card">
                    {meta.data ? (
                        <>
                            <p>
                                <strong>{localized(meta.data.name)}</strong> · v{meta.data.version}{' '}
                                · {formatDate(meta.data.updated)}
                            </p>
                            <p>{localized(meta.data.description)}</p>
                            <p>
                                <strong>{t('common.demoNotice')}</strong>
                            </p>
                            <p style={{ color: 'var(--text-muted)' }}>
                                {localized(meta.data.disclaimer)}
                            </p>
                            <h3>{t('about.sources')}</h3>
                            <ul>
                                {meta.data.sources.map((source) => (
                                    <li key={source}>
                                        <a href={source} target="_blank" rel="noreferrer noopener">
                                            {source}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </>
                    ) : (
                        <p className="empty">{t('common.loading')}</p>
                    )}
                </div>
            </Section>

            <Section title={t('about.apiTitle')}>
                <div className="card">
                    <p>{t('about.api')}</p>
                    <p>
                        <a href="/docs" target="_blank" rel="noreferrer noopener">
                            /docs
                        </a>{' '}
                        ·{' '}
                        <a href="/openapi.json" target="_blank" rel="noreferrer noopener">
                            /openapi.json
                        </a>
                    </p>
                    <ul>
                        {endpoints.map((endpoint) => (
                            <li key={endpoint}>
                                <a href={endpoint} target="_blank" rel="noreferrer noopener">
                                    <code>{endpoint}</code>
                                </a>
                            </li>
                        ))}
                    </ul>
                    <h3>{t('about.backend')}</h3>
                    <div className="chips">
                        <Chip variant={health.data ? 'in-use' : 'to-be-replaced'}>
                            {health.data?.status ?? 'unavailable'}
                        </Chip>
                        {health.data ? (
                            <>
                                <Chip variant="outline">
                                    {health.data.fips} {t('nav.profiles').toLowerCase()}
                                </Chip>
                                <Chip variant="outline">
                                    {health.data.resources} {t('resources.count')}
                                </Chip>
                            </>
                        ) : null}
                    </div>
                </div>
            </Section>
        </PageShell>
    )
}
